import type { DecisionOutcome } from './useKycDecisionDraft';
import type { AvailableAction, Parecer, ParecerDecision, ParecerStage, ParecerUiManifest } from '@/api/modules/parecerApi';

export type ParecerDecisionLane = 'proposal' | 'final';
export type ParecerStageKind = ParecerStage;

export interface ParecerHydratedRecord {
  parecer: Parecer;
  uiManifest: ParecerUiManifest | null;
  /** Only for a parecer in the unassigned queue: the outbound transition that takes it. */
  takeTransition?: AvailableAction | null;
}

/**
 * Whether the signed-in user may take a queued parecer, straight from the workflow engine.
 * `blockedReason` is the backend's pt-BR explanation, ready to use as a tooltip.
 */
export interface ParecerTakeTransition {
  toStageSlug: string;
  allowed: boolean;
  blockedReason: string | null;
}

export interface ParecerDecisionStatus {
  parecerId: string;
  requestId: string;
  lane: ParecerDecisionLane;
  decision: DecisionOutcome;
  relationshipTypes: string[];
  decidedAt: string;
  decidedByName?: string | null;
  expirationDate: string | null;
  riskLevel?: string | null;
  isExpired: boolean;
}

export interface OngoingParecerStatus {
  parecerId: string;
  requestId: string;
  stageKind: ParecerStageKind;
  stageName: string;
  activeLane: ParecerDecisionLane | null;
  previewDecision: ParecerDecisionStatus | null;
  updatedAt: string;
  /** Present only while the parecer sits in the unassigned queue. */
  takeTransition?: ParecerTakeTransition | null;
}

export interface ParecerWorkflowStatus {
  parecerId: string;
  requestId: string;
  currentStage: {
    slug: ParecerStageKind;
    name: string;
  };
  activeLane: ParecerDecisionLane | null;
  proposalDecision: ParecerDecisionStatus | null;
  finalDecision: ParecerDecisionStatus | null;
  updatedAt: string;
  createdBy?: 'user' | 'automation' | null;
  policyDecision?: 'approve' | 'decline' | 'manual' | null;
  responsibleName?: string | null;
  reportRiskLevel?: string | null;
  entityType?: 'person' | 'company';
  openedAt?: string;
  /** Present only while the parecer sits in the unassigned queue. */
  takeTransition?: ParecerTakeTransition | null;
}

export interface EntityParecerStatus {
  currentParecer: ParecerWorkflowStatus | null;
  latestFinalizedParecer: ParecerWorkflowStatus | null;
  hasAnyParecer: boolean;
}

type OutcomeTheme = {
  bg: string;
  border: string;
  text: string;
  icon: string;
  iconColor: string;
  pillBg: string;
};

const toTimestamp = (dateValue?: string | null) => {
  if (!dateValue) return 0;
  const parsed = new Date(dateValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isApprovedDecision = (decision: DecisionOutcome) =>
  decision === 'approved' || decision === 'approved_with_conditions';

export const isExpiredDecision = (
  decision: DecisionOutcome,
  expirationDate?: string | null,
): boolean => {
  if (!isApprovedDecision(decision) || !expirationDate) return false;

  const reviewDate = new Date(expirationDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return reviewDate < today;
};

export type ParecerAutomationKind = 'auto_finalized' | 'auto_manual_pending' | 'auto_origin';

export interface ParecerAutomationInfo {
  kind: ParecerAutomationKind;
  label: string;
  tone: 'neutral' | 'attention';
}

/**
 * Classifies how the decision-policy automation is involved in a parecer, from the two
 * fields the backend stamps at open time: `created_by` ('automation' when the policy opened
 * it) and `policy_decision` (what the policy decided — approve / decline / manual).
 *
 * - auto_finalized: the policy approved/declined and closed the parecer itself.
 * - auto_manual_pending: the policy could not decide and routed it to a human; still open.
 * - auto_origin: policy-opened but already finalized by a human (manual route, decided).
 *
 * Returns null when a human opened the parecer.
 */
export const getParecerAutomation = (
  createdBy: string | null | undefined,
  policyDecision: string | null | undefined,
  isFinalized: boolean,
): ParecerAutomationInfo | null => {
  if (createdBy !== 'automation') return null;
  if (policyDecision === 'approve' || policyDecision === 'decline') {
    return { kind: 'auto_finalized', label: 'Decisão automática', tone: 'neutral' };
  }
  if (policyDecision === 'manual' && !isFinalized) {
    return { kind: 'auto_manual_pending', label: 'Requer decisão manual', tone: 'attention' };
  }
  return { kind: 'auto_origin', label: 'Aberto pela automação', tone: 'neutral' };
};

export const getParecerOutcomeTheme = (decision: DecisionOutcome): OutcomeTheme => {
  switch (decision) {
    case 'approved':
      return {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-800',
        icon: 'verified',
        iconColor: 'text-emerald-600',
        pillBg: 'bg-emerald-100',
      };
    case 'approved_with_conditions':
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-800',
        icon: 'gavel',
        iconColor: 'text-amber-600',
        pillBg: 'bg-amber-100',
      };
    case 'rejected':
      return {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-800',
        icon: 'block',
        iconColor: 'text-rose-600',
        pillBg: 'bg-rose-100',
      };
    case 'rfi':
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'help_outline',
        iconColor: 'text-blue-600',
        pillBg: 'bg-blue-100',
      };
    case 'escalated':
      return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        icon: 'priority_high',
        iconColor: 'text-orange-600',
        pillBg: 'bg-orange-100',
      };
    default:
      return {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-800',
        icon: 'help',
        iconColor: 'text-gray-600',
        pillBg: 'bg-gray-100',
      };
  }
};

export const getActiveParecerLane = (
  uiManifest?: ParecerUiManifest | null,
): ParecerDecisionLane | null => {
  const finalField = uiManifest?.fields?.decision_final;
  const proposalField = uiManifest?.fields?.decision_proposal;

  if (finalField?.editable) return 'final';
  if (proposalField?.editable) return 'proposal';
  if (finalField?.visible && !proposalField?.visible) return 'final';
  if (proposalField?.visible && !finalField?.visible) return 'proposal';
  if (finalField?.visible) return 'final';
  if (proposalField?.visible) return 'proposal';
  return null;
};

export const getCurrentParecerDecision = (
  currentParecer: ParecerWorkflowStatus | null,
): ParecerDecisionStatus | null => {
  if (!currentParecer) return null;

  if (currentParecer.currentStage.slug === 'finalized') {
    return currentParecer.finalDecision;
  }

  if (currentParecer.currentStage.slug === 'manager_review') {
    return currentParecer.proposalDecision;
  }

  if (currentParecer.activeLane === 'final') {
    return currentParecer.finalDecision ?? currentParecer.proposalDecision;
  }

  return currentParecer.proposalDecision ?? currentParecer.finalDecision;
};

const toDecisionStatus = (
  parecer: Parecer,
  lane: ParecerDecisionLane,
  decision: ParecerDecision | null | undefined,
): ParecerDecisionStatus | null => {
  if (!decision?.decision) return null;

  return {
    parecerId: parecer.id,
    requestId: parecer.report_request_id,
    lane,
    decision: decision.decision,
    relationshipTypes: (parecer.relationship_types ?? [])
      .map((relationshipType) => relationshipType.slug)
      .filter((slug): slug is string => Boolean(slug)),
    decidedAt: decision.decided_at || parecer.updated_at || parecer.inserted_at,
    decidedByName: decision.decided_by_user_name ?? null,
    expirationDate: decision.expiration_date || null,
    riskLevel: decision.risk_level || null,
    isExpired: isExpiredDecision(decision.decision, decision.expiration_date),
  };
};

// `AvailableAction` keeps the API's word (GET /actions); everything on this side of the
// boundary calls it what it is — one outbound transition.
const toTakeTransition = (
  transition: AvailableAction | null | undefined,
): ParecerTakeTransition | null => {
  if (!transition) return null;
  return {
    toStageSlug: transition.to_stage_slug,
    allowed: !transition.is_disabled,
    blockedReason: transition.is_disabled ? transition.reason ?? null : null,
  };
};

const compareMostRecentParecer = (a: Parecer, b: Parecer) =>
  toTimestamp(b.updated_at || b.inserted_at) - toTimestamp(a.updated_at || a.inserted_at);

const toParecerWorkflowStatus = (
  record: ParecerHydratedRecord,
): ParecerWorkflowStatus => ({
  parecerId: record.parecer.id,
  requestId: record.parecer.report_request_id,
  currentStage: {
    slug: record.parecer.current_stage.slug,
    name: record.parecer.current_stage.name,
  },
  activeLane: getActiveParecerLane(record.uiManifest),
  proposalDecision: toDecisionStatus(record.parecer, 'proposal', record.parecer.decisions?.proposal),
  finalDecision: toDecisionStatus(record.parecer, 'final', record.parecer.final_decision),
  updatedAt: record.parecer.updated_at || record.parecer.inserted_at,
  createdBy: record.parecer.created_by ?? null,
  policyDecision: record.parecer.policy_decision ?? null,
  responsibleName: record.parecer.assigned_user_name ?? null,
  reportRiskLevel: record.parecer.report_risk_level ?? null,
  entityType: record.parecer.entity_type,
  openedAt: record.parecer.inserted_at,
  takeTransition: toTakeTransition(record.takeTransition),
});

export const buildEntityParecerStatus = (
  records: ParecerHydratedRecord[],
  preferredRequestId?: string | null,
): EntityParecerStatus => {
  if (!records.length) {
    return {
      currentParecer: null,
      latestFinalizedParecer: null,
      hasAnyParecer: false,
    };
  }

  const sortedRecords = [...records].sort((a, b) => compareMostRecentParecer(a.parecer, b.parecer));
  const latestFinalizedParecer = sortedRecords
    .filter((record) => record.parecer.current_stage.slug === 'finalized' && record.parecer.final_decision?.decision)
    .map(toParecerWorkflowStatus)[0] ?? null;

  const currentCandidates = [...sortedRecords].sort((left, right) => {
    const leftPreferred = preferredRequestId && left.parecer.report_request_id === preferredRequestId ? 1 : 0;
    const rightPreferred = preferredRequestId && right.parecer.report_request_id === preferredRequestId ? 1 : 0;
    if (leftPreferred !== rightPreferred) {
      return rightPreferred - leftPreferred;
    }

    const leftIsActive = left.parecer.current_stage.slug !== 'finalized' ? 1 : 0;
    const rightIsActive = right.parecer.current_stage.slug !== 'finalized' ? 1 : 0;
    if (leftPreferred === 0 && rightPreferred === 0 && leftIsActive !== rightIsActive) {
      return rightIsActive - leftIsActive;
    }

    return compareMostRecentParecer(left.parecer, right.parecer);
  });

  const currentParecer = currentCandidates[0] ? toParecerWorkflowStatus(currentCandidates[0]) : null;

  return {
    currentParecer,
    latestFinalizedParecer,
    hasAnyParecer: true,
  };
};
